import { useQueryScheduledVersion } from '@ben/columbia/hooks/use-query-scheduled-version';
import BackHeader from '@ben/orion/back-header';
import CopyIcon from '@ben/orion/copy-icon';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import MantineTooltip from '@ben/orion/mantine-tooltip';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { decodeHtmlEntities } from '@ben/shared/utils/decode-html-entities';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';

interface PageHeaderProps {
  isSharedBenefit: boolean;
  isCreateBenefit: boolean;
  isCloneBenefit: boolean;
  isScheduledVersion: boolean;
  requiresApproval: boolean;
  benefitId?: string;
  benefitName?: string;
  onClickClone?: () => void;
  onClickAddNewVersion?: () => void;
}

const PageHeader = ({
  isSharedBenefit,
  isCreateBenefit,
  isCloneBenefit,
  isScheduledVersion,
  requiresApproval,
  benefitId,
  benefitName,
  onClickClone,
  onClickAddNewVersion,
}: PageHeaderProps) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditBenefit = !isCreateBenefit && !isCloneBenefit && !isSharedBenefit;

  const { data: scheduledVersion } = useQueryScheduledVersion(benefitId, {
    enabled: !isCreateBenefit,
  });
  const hasScheduledVersion = !!scheduledVersion?.enrollableBenefit?.id;

  const pageTitle = useMemo(() => {
    if (isCreateBenefit) {
      return 'Create Benefit';
    }
    if (isCloneBenefit) {
      return `Clone Benefit ${decodeHtmlEntities(benefitName || '')}`;
    }
    return 'Edit Benefit';
  }, [benefitName, isCloneBenefit, isCreateBenefit]);

  const onClickBack = useCallback(() => {
    const search = new URLSearchParams(location.search).get('search');
    navigate({
      pathname: '/benefits/',
      search: search ? `?search=${search}` : undefined,
    });
  }, [navigate, location.search]);

  const addNewVersionTooltip = useMemo(() => {
    if (hasScheduledVersion) {
      return 'You can only have one active benefit version and one scheduled version at a time.';
    }
    if (requiresApproval) {
      return 'You cannot add a version of a benefit that requires approval.';
    }
    return 'Add a new version to the benefit you’re editing, perfect for including renewal details or other updates that have a scheduled start date.';
  }, [requiresApproval, hasScheduledVersion]);

  return (
    <Wrapper>
      <BackHeader title="Back to benefits" onBack={onClickBack} noMargin />
      <ContentWrapper>
        <div>
          <Typography variant={TypographyVariant.h1}>{pageTitle}</Typography>
          {isEditBenefit && benefitId && (
            <BenefitIdWrapper>
              <Typography
                variant={TypographyVariant.bodyLg3}
                color={theme.palette.neutral[90]}
              >
                #{benefitId}
              </Typography>
              <CopyIcon value={String(benefitId)} />
            </BenefitIdWrapper>
          )}
        </div>
        <ButtonWrapper>
          {isEditBenefit && onClickClone && (
            <MantineTooltip
              label="Create a new benefit starting from this one. Useful for assigning it to another employer or to create a similar benefit."
              position="bottom"
            >
              <Button
                label="Clone benefit"
                onClick={onClickClone}
                variant={ButtonVariant.secondary}
                width="auto"
                disabled={isScheduledVersion}
              />
            </MantineTooltip>
          )}
          {isEditBenefit && (
            <MantineTooltip label={addNewVersionTooltip} position="bottom">
              <Button
                label="Add new version"
                onClick={onClickAddNewVersion}
                variant={ButtonVariant.primary}
                width="auto"
                disabled={hasScheduledVersion || requiresApproval}
                style={{ pointerEvents: 'auto' }}
              />
            </MantineTooltip>
          )}
        </ButtonWrapper>
      </ContentWrapper>
    </Wrapper>
  );
};

export default PageHeader;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(5)};
  `}
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ButtonWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(2)};
  `}
`;

const BenefitIdWrapper = styled.div`
  display: flex;
  align-items: center;
`;
